import { ArrowBack } from '@mui/icons-material';
import { Box, Typography, useMediaQuery } from '@mui/material';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button } from '@/components';
import { Regions, Routes, urls } from '@/constants';
import { AppFooter } from '@/containers';
import { lumiqHorizontalLogo, lumiqMonoIconLogo } from '@/images';
import { AnalyticsService, EVENT_CONSTANTS, EVENTS } from '@/services';
import {
  selectSessionUserId,
  selectUserRegion,
  updateRegion,
  useAppDispatch,
  useAppSelector,
} from '@/store';
import { Region } from '@/types';

import { PricingTable, PromoCode } from './components';

export const Pricing = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('md'));

  const icons = {
    mobile: lumiqMonoIconLogo,
    desktop: lumiqHorizontalLogo,
  };

  const userId = useAppSelector(selectSessionUserId);
  const region = useAppSelector(selectUserRegion);

  const { region: queryRegion } = Object.fromEntries(
    new URLSearchParams(location.search).entries(),
  );

  useEffect(() => {
    AnalyticsService.logEvent(EVENTS.MARKETING_EVENTS.VIEW_PRICING);
  }, []);

  useEffect(() => {
    if (userId || typeof queryRegion !== 'string') return;

    const formattedQueryRegion = queryRegion.toUpperCase() as Region;

    if (Object.keys(Regions).includes(formattedQueryRegion)) {
      dispatch(
        updateRegion.fulfilled({ region: formattedQueryRegion }, '', {
          region: formattedQueryRegion,
        }),
      );
    }
  }, [dispatch, userId, queryRegion]);

  return (
    <Box
      sx={{
        background: theme => theme.palette.gradients.pricingBackground,
        minHeight: '100vh',
        width: '100%',
      }}>
      <Box sx={{ p: { xs: 3, md: 6 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            mb: {
              xs: 6,
              md: 8,
            },
          }}>
          <Button
            label={isMobile ? 'Back' : 'Back To LumiQ App'}
            variant="tertiary"
            fullWidth={false}
            skinny={false}
            startIcon={<ArrowBack />}
            onClick={() => navigate(Routes.DISCOVER)}
            sx={{
              position: 'absolute',
              left: 0,
            }}
          />
          <Box
            component="img"
            alt="lumiq logo"
            sx={{ height: 42 }}
            src={isMobile ? icons.mobile : icons.desktop}
          />
        </Box>
        <Typography
          variant="hero4"
          sx={{ mx: 'auto', width: 'fit-content', mb: 4, color: 'text.main' }}>
          Select Your Plan
        </Typography>
        <Box
          sx={{
            maxWidth: 1400,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}>
          <PromoCode />
          <Box
            sx={{
              position: 'relative',
              borderRadius: 4,
              boxShadow: '3px 3px 10px 0 rgba(0, 0, 0, 0.25)',
              overflow: 'hidden',
              backgroundColor: 'white',
            }}>
            <Box sx={{ p: { xs: 3, md: 4 } }}>
              <PricingTable />
            </Box>
            <Box
              sx={{
                backgroundColor: 'secondary.medium',
                width: '100%',
                p: { xs: 3, md: 6 },
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 3,
                justifyContent: 'center',
              }}>
              <Box>
                <Typography variant="h4" sx={{ mb: 1 }}>
                  See what’s included in each plan.
                </Typography>
                <Typography variant="body2">
                  Compare benefits and features to find the perfect plan for you
                  or your team.
                </Typography>
              </Box>
              <Button
                label="See All Plan Details"
                skinny={false}
                fullWidth={false}
                variant="secondary"
                sx={{ backgroundColor: 'white' }}
                onClick={() => {
                  AnalyticsService.buttonClick({
                    name: EVENT_CONSTANTS.BUTTON.SEE_PRICING_DETAILS,
                  });
                  window.open(urls.pricingDetails[region], '_blank');
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
      <AppFooter showBorder={false} />
    </Box>
  );
};
